export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

export const validateEmail = (email: string): ValidationResult => {
  if (!email || !email.trim()) {
    return { isValid: false, error: 'Email is required' };
  }

  const emailTrimmed = email.trim().toLowerCase();
  
  // Check for basic format first
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(emailTrimmed)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }

  // Check for common typos
  const commonTypos = ['gmial.com', 'gmai.com', 'yahooo.com', 'hotmial.com'];
  const domain = emailTrimmed.substring(emailTrimmed.lastIndexOf('@') + 1);
  if (commonTypos.includes(domain)) {
    return { isValid: false, error: 'Please check your email address for typos' };
  }
  const validDomains = [
    '.com', '.org', '.net', '.edu', '.gov', '.mil', '.int',
    '.ca', '.uk', '.au', '.de', '.fr', '.jp', '.cn', '.in', '.br',
    '.mx', '.it', '.es', '.nl', '.se', '.no', '.dk', '.fi', '.ch',
    '.at', '.be', '.ie', '.pt', '.gr', '.pl', '.cz', '.hu', '.ro',
    '.bg', '.hr', '.si', '.sk', '.lt', '.lv', '.ee', '.is', '.mt',
    '.info', '.biz', '.name', '.pro', '.mobi', '.tel', '.travel',
    '.museum', '.coop', '.aero', '.jobs', '.cat', '.asia', '.xxx',
    '.post', '.geo'
  ];

  const fullDomain = emailTrimmed.substring(emailTrimmed.lastIndexOf('@'));
  const hasValidDomain = validDomains.some(validDomain => 
    fullDomain.endsWith(validDomain)
  );

  if (!hasValidDomain) {
    return { 
      isValid: false, 
      error: 'Please enter an email with a valid domain (e.g., .com, .org, .ca)' 
    };
  }

  const domainPart = fullDomain.substring(1);
  const domainWithoutExtension = domainPart.substring(0, domainPart.lastIndexOf('.'));
  
  if (/^[a-z0-9]{3,}$/.test(domainWithoutExtension) && domainWithoutExtension.length > 8) {
    const hasPattern = /(.)\1{2,}/.test(domainWithoutExtension) || 
                      /^[0-9]+$/.test(domainWithoutExtension) ||
                      /^[a-z]+[0-9]+$/.test(domainWithoutExtension);
    if (hasPattern) {
      return { 
        isValid: false, 
        error: 'Please enter a valid email address from a recognized provider' 
      };
    }
  }

  return { isValid: true };
};

export const validatePhoneNumber = (phone: string, format?: string): ValidationResult => {
  if (!phone || !phone.trim()) {
    return { isValid: false, error: 'Phone number is required' };
  }

  const phoneTrimmed = phone.replace(/[^\d+\-()]/g, '');
  
  if (phone.includes(' ')) {
    return { 
      isValid: false, 
      error: 'Phone number cannot contain spaces' 
    };
  }
  
  const cleanDigits = phoneTrimmed.replace(/\D/g, '');
  if (cleanDigits.length < 10 || cleanDigits.length > 15) {
    return { 
      isValid: false, 
      error: 'Phone number must be between 10-15 digits' 
    };
  }

  const phoneFormats = [
    /^\+?1?[-.]?\(?[0-9]{3}\)?[-.]?[0-9]{3}[-.]?[0-9]{4}$/,
    /^\+?[1-9]\d{1,14}$/,
    /^\(?[0-9]{3}\)?[-.]?[0-9]{3}[-.]?[0-9]{4}$/,
  ];

  const isValidFormat = phoneFormats.some(formatRegex => formatRegex.test(phoneTrimmed));
  
  if (!isValidFormat) {
    return { 
      isValid: false, 
      error: 'Please enter a valid phone number (e.g., +1-555-123-4567)' 
    };
  }

  return { isValid: true };
};

export const validateName = (name: string, fieldName: string): ValidationResult => {
  if (!name || !name.trim()) {
    return { isValid: false, error: `${fieldName} is required` };
  }

  const nameTrimmed = name.trim();
  
  if (nameTrimmed.length < 2) {
    return { 
      isValid: false, 
      error: `${fieldName} must be at least 2 characters long` 
    };
  }

  const nameRegex = /^[a-zA-Z\s\-'.]+$/;
  if (!nameRegex.test(nameTrimmed)) {
    return { 
      isValid: false, 
      error: `${fieldName} can only contain letters, spaces, hyphens, apostrophes, and periods` 
    };
  }

  const meaningfulContent = /[a-zA-Z]/.test(nameTrimmed);
  if (!meaningfulContent) {
    return { 
      isValid: false, 
      error: `${fieldName} must contain at least one letter` 
    };
  }

  return { isValid: true };
};

export const validatePassword = (password: string): ValidationResult => {
  if (!password) {
    return { isValid: false, error: 'Password is required' };
  }

  if (password.length < 8) {
    return { 
      isValid: false, 
      error: 'Password must be at least 8 characters long' 
    };
  }

  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
    return { 
      isValid: false, 
      error: 'Password must contain uppercase, lowercase, number, and special character' 
    };
  }

  return { isValid: true };
};

export const sanitizeInput = (input: string): string => {
  if (!input) return '';
  
  return input
    .trim()
    .replace(/[<>]/g, '')
    .replace(/['"]/g, '')
    .replace(/\s+/g, ' ');
};

export const validateRequired = (value: string, fieldName: string): ValidationResult => {
  if (!value || !value.trim()) {
    return { isValid: false, error: `${fieldName} is required` };
  }
  
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return { isValid: false, error: `${fieldName} cannot be empty` };
  }
  
  return { isValid: true };
};

export const validateZipCode = (zipCode: string, country: string = 'United States'): ValidationResult => {
  if (!zipCode || !zipCode.trim()) {
    return { isValid: false, error: 'ZIP code is required' };
  }

  const zipTrimmed = zipCode.trim();
  
  // US ZIP code validation (5 digits or 5+4 format)
  if (country === 'United States') {
    const usZipRegex = /^[0-9]{5}(-[0-9]{4})?$/;
    if (!usZipRegex.test(zipTrimmed)) {
      return { 
        isValid: false, 
        error: 'Please enter a valid US ZIP code (e.g., 12345 or 12345-6789)' 
      };
    }
  }
  // Canadian postal code validation
  else if (country === 'Canada') {
    const canadaPostalRegex = /^[A-Za-z][0-9][A-Za-z][ -]?[0-9][A-Za-z][0-9]$/;
    if (!canadaPostalRegex.test(zipTrimmed)) {
      return { 
        isValid: false, 
        error: 'Please enter a valid Canadian postal code (e.g., K1A 0A6)' 
      };
    }
  }
  // UK postal code validation
  else if (country === 'United Kingdom') {
    const ukPostalRegex = /^[A-Za-z]{1,2}[0-9Rr][0-9A-Za-z]?[ ]?[0-9][ABD-HJLNP-UW-Zabd-hjlnp-uw-z]{2}$/;
    if (!ukPostalRegex.test(zipTrimmed)) {
      return { 
        isValid: false, 
        error: 'Please enter a valid UK postal code (e.g., SW1A 1AA)' 
      };
    }
  }
  // Generic validation for other countries (3-10 alphanumeric characters)
  else {
    const genericPostalRegex = /^[A-Za-z0-9\s\-]{3,10}$/;
    if (!genericPostalRegex.test(zipTrimmed)) {
      return { 
        isValid: false, 
        error: 'Please enter a valid postal code' 
      };
    }
  }

  return { isValid: true };
};